import { Box, Stack } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import SearchBar from "components/SearchBar";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { CheckpointTemplate } from "features/Resources/CheckpointTemplate/models";
import { useMemo, useState } from "react";
import { useCheckpointTemplateListQuery } from "features/Resources/CheckpointTemplate/hooks";
import { InternalLink } from "components/Link";
import { Text12 } from "components/Text";
import { createQueryString } from "helpers/queryString";
import { checkpointTemplatePathById } from "helpers/url/pages/v2/actionPaths";
import TemplateListEmptyState from "features/Resources/SharedTemplate/Components/TemplateListEmptyState";
import { TemplateTableDeleteButton } from "features/Resources/SharedTemplate/Components/TemplateTableButtons";
import { DisplayTemplatePaths } from "features/Resources/SharedTemplate/Components/DisplayTemplatePaths";
import { DisplayCheckpointRecipients } from "features/Resources/SharedTemplate/Components/DisplayTemplateRecipients";
import TriggerTiming from "components/Display/TriggerTiming";
import { CHECK_TARGET_LABELS, DELIVERY_METHOD_LABELS } from "features/Resources/Shared/constants";

const columnHelper = createColumnHelper<CheckpointTemplate>();

const columns = [
  columnHelper.accessor((row) => row, {
    id: "name",
    header: "Name",
    enableSorting: false,
    cell: (row) => (
      <Box width="fit-content">
        <InternalLink
          to={checkpointTemplatePathById(row.getValue().id)}
          textDecoration="none"
          _hover={{ textDecoration: "none" }}
          width="fit-content"
        >
          <Text12 wordBreak="break-word" fontWeight="bold">
            {row.getValue()?.name}
          </Text12>
        </InternalLink>
      </Box>
    ),
    meta: {
      sx: {
        paddingLeft: 0,
      },
      headerHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "scheduled",
    header: "When",
    enableSorting: false,
    cell: (row) => <TriggerTiming descriptor={row.getValue().evaluateAtTrigger?.data?.descriptor} />,
    meta: {
      sx: {
        width: "160px",
        maxWidth: "160px",
        minWidth: "160px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "checkTarget",
    header: "Check",
    enableSorting: false,
    cell: (row) => <Text12>{CHECK_TARGET_LABELS[row.getValue().checkTarget] || row.getValue().checkTarget}</Text12>,
    meta: {
      sx: {
        width: "160px",
        maxWidth: "160px",
        minWidth: "160px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "deliveryMethod",
    header: "Deliver Via",
    enableSorting: false,
    cell: (row) => (
      <Text12>{DELIVERY_METHOD_LABELS[row.getValue().deliveryMethod] || row.getValue().deliveryMethod}</Text12>
    ),
    meta: {
      sx: {
        width: "120px",
        maxWidth: "120px",
        minWidth: "120px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "notify",
    header: "Notify",
    enableSorting: false,
    cell: (row) => <DisplayCheckpointRecipients recipients={row.getValue().recipients} />,
    meta: {
      sx: {
        width: "120px",
        maxWidth: "120px",
        minWidth: "120px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "pathIds",
    header: "Paths",
    enableSorting: false,
    cell: (row) => <DisplayTemplatePaths template={row.getValue()} />,
    meta: {
      sx: {
        width: "100px",
        maxWidth: "100px",
        minWidth: "100px",
      },
      headerHStackProps: { justifyContent: "flex-start" },
      cellHStackProps: { justifyContent: "flex-start" },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "buttons",
    header: "",
    enableSorting: false,
    cell: (row) => (
      <TemplateTableDeleteButton templateSummary={{ templateType: "checkpoint_template", template: row.getValue() }} />
    ),
    meta: {
      sx: {
        width: "60px",
        maxWidth: "60px",
        minWidth: "60px",
      },
      headerHStackProps: { justifyContent: "flex-end" },
      cellHStackProps: { justifyContent: "flex-end" },
    },
  }),
];

export default function ListCheckpointTemplates() {
  const [searchText, setSearchText] = useState("");

  const queryString = useMemo(
    () =>
      createQueryString({
        pagination: { pageIndex: 0, pageSize: 200 },
        additionalQueries: {
          q: searchText && searchText.length > 2 ? searchText : undefined,
        },
      }),
    [searchText],
  );

  const { data } = useCheckpointTemplateListQuery(queryString);

  return (
    <Stack direction="column" gap="4" w="100%">
      <Stack direction="row" w="100%" alignItems="center" justifyContent="flex-start" gap="2">
        <SearchBar minWidth="500" onInput={setSearchText} />
      </Stack>

      <ControlledDataTable<CheckpointTemplate>
        data={data || []}
        columns={columns}
        initialPagination={{ pageSize: 200, pageIndex: 0 }}
        emptyStateComponent={<TemplateListEmptyState actionType="checkpoint" hasActiveFilters={searchText.length > 2} />}
      />
    </Stack>
  );
}
